import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckSquare, Plus, Trash2, ChevronDown, ChevronUp, User, Link2, Flag, X, FileText } from "lucide-react";
import { taskAPI, clientAPI } from "@/lib/api";
import { toast } from "sonner";
import { DateField } from "@/components/ui/date-field";

const PRIORITY_COLORS = {
  haute: "bg-red-100 text-red-700 border-red-200",
  normale: "bg-slate-100 text-slate-600 border-slate-200",
  basse: "bg-sky-100 text-sky-700 border-sky-200",
};

const PRIORITY_DOT = { haute: "bg-red-500", normale: "bg-slate-400", basse: "bg-sky-400" };

export function TaskButton({ count, onClick }) {
  return (
    <button
      onClick={onClick}
      className="relative p-2 rounded-lg hover:bg-slate-100 transition-colors"
      title="Taches"
      data-testid="task-panel-btn"
    >
      <CheckSquare size={20} className="text-slate-600" />
      {count > 0 && (
        <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold px-1" data-testid="task-count-badge">
          {count > 99 ? "99+" : count}
        </span>
      )}
    </button>
  );
}

export function TaskPanel({ open, onOpenChange, isAdmin, currentUserId, prefill }) {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [showAll, setShowAll] = useState(false);
  const [showDone, setShowDone] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [expandedNew, setExpandedNew] = useState(false);
  const [newForm, setNewForm] = useState({ description: "", due_date: "", priority: "normale", assigned_to: "", client_id: "", client_name: "", document_id: "", document_number: "" });
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [clientSearch, setClientSearch] = useState("");
  const [clientResults, setClientResults] = useState([]);

  // Apply prefill when panel opens with prefill data
  useEffect(() => {
    if (open && prefill) {
      setNewForm(p => ({
        ...p,
        client_id: prefill.client_id || "",
        client_name: prefill.client_name || "",
        document_id: prefill.document_id || "",
        document_number: prefill.document_number || "",
      }));
      setExpandedNew(true);
    }
  }, [open, prefill]);

  const navigateTo = (path) => {
    onOpenChange(false);
    setTimeout(() => navigate(path), 150);
  };

  const loadTasks = useCallback(async () => {
    try {
      const r = await taskAPI.list(showAll);
      setTasks(r.data || []);
    } catch (e) {
      toast.error("Erreur chargement tâches");
    }
  }, [showAll]);

  useEffect(() => {
    if (open) {
      loadTasks();
      taskAPI.users().then(r => setUsers(r.data || [])).catch(() => {});
    }
  }, [open, loadTasks]);

  // Search clients for linking
  useEffect(() => {
    if (clientSearch.length < 2) { setClientResults([]); return; }
    const timer = setTimeout(async () => {
      try {
        const r = await clientAPI.list({ search: clientSearch });
        setClientResults((r.data?.items || r.data || []).slice(0, 5));
      } catch { setClientResults([]); }
    }, 300);
    return () => clearTimeout(timer);
  }, [clientSearch]);

  const selectClient = (c) => {
    setNewForm(p => ({ ...p, client_id: c.id, client_name: c.company || `${c.first_name || ""} ${c.last_name || ""}`.trim(), document_id: "", document_number: "" }));
    setClientSearch("");
    setClientResults([]);
  };

  const clearClient = () => {
    setNewForm(p => ({ ...p, client_id: "", client_name: "", document_id: "", document_number: "" }));
  };

  const pendingTasks = tasks.filter(t => !t.done);
  const doneTasks = tasks.filter(t => t.done);

  const handleAdd = async () => {
    if (!newTitle.trim()) return;
    setLoading(true);
    try {
      await taskAPI.create({ title: newTitle.trim(), ...newForm });
      setNewTitle("");
      setNewForm({ description: "", due_date: "", priority: "normale", assigned_to: "", client_id: "", client_name: "", document_id: "", document_number: "" });
      setExpandedNew(false);
      await loadTasks();
      toast.success("Tâche ajoutée");
    } catch (e) {
      toast.error("Erreur lors de l'ajout");
    }
    setLoading(false);
  };

  const handleToggle = async (taskId) => {
    try {
      await taskAPI.toggle(taskId);
      setTasks(prev => prev.map(t => t.id === taskId ? { ...t, done: !t.done } : t));
    } catch (e) {
      toast.error("Erreur");
    }
  };

  const handleDelete = async (taskId) => {
    try {
      await taskAPI.delete(taskId);
      setTasks(prev => prev.filter(t => t.id !== taskId));
      toast.success("Tâche supprimée");
    } catch (e) {
      toast.error("Erreur");
    }
  };

  const formatDate = (d) => {
    if (!d) return null;
    const date = new Date(d + "T00:00:00");
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diff = Math.floor((date - today) / 86400000);
    const formatted = date.toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
    if (diff < 0) return { text: formatted, color: "text-red-500" };
    if (diff === 0) return { text: "Aujourd'hui", color: "text-amber-600" };
    if (diff === 1) return { text: "Demain", color: "text-amber-500" };
    return { text: formatted, color: "text-slate-400" };
  };

  const TaskItem = ({ task }) => {
    const dateInfo = formatDate(task.due_date);
    const isOverdue = dateInfo?.color === "text-red-500" && !task.done;
    return (
      <div className={`group flex items-start gap-2.5 py-2.5 px-3 rounded-lg transition-colors hover:bg-slate-50 ${isOverdue ? "bg-red-50/50" : ""}`} data-testid={`task-item-${task.id}`}>
        <button
          onClick={() => handleToggle(task.id)}
          className={`mt-0.5 flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${task.done ? "bg-emerald-500 border-emerald-500 text-white" : "border-slate-300 hover:border-indigo-400"}`}
          data-testid={`task-toggle-${task.id}`}
        >
          {task.done && <CheckSquare size={12} />}
        </button>
        <div className="flex-1 min-w-0">
          <p className={`text-sm leading-snug ${task.done ? "line-through text-slate-400" : "text-slate-800"}`}>{task.title}</p>
          {task.description && <p className="text-xs text-slate-400 mt-0.5 line-clamp-1">{task.description}</p>}
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <span className={`inline-block w-1.5 h-1.5 rounded-full ${PRIORITY_DOT[task.priority]}`} title={task.priority} />
            {dateInfo && <span className={`text-[11px] ${dateInfo.color}`}>{dateInfo.text}</span>}
            {task.client_name && task.client_id && (
              <button onClick={() => navigateTo(`/clients/${task.client_id}`)} className="text-[11px] text-indigo-500 hover:text-indigo-700 hover:underline flex items-center gap-0.5" data-testid={`task-client-link-${task.id}`}>
                <Link2 size={9} />{task.client_name}
              </button>
            )}
            {task.document_number && (
              <span className="text-[11px] text-emerald-600 flex items-center gap-0.5" data-testid={`task-doc-link-${task.id}`}>
                <FileText size={9} />{task.document_number}
              </span>
            )}
            {task.assigned_to !== currentUserId && task.assigned_to_name && (
              <span className="text-[11px] text-slate-400 flex items-center gap-0.5"><User size={9} />{task.assigned_to_name}</span>
            )}
          </div>
        </div>
        <button
          onClick={() => handleDelete(task.id)}
          className="flex-shrink-0 opacity-0 group-hover:opacity-100 p-1 text-slate-300 hover:text-red-500 transition-all"
          data-testid={`task-delete-${task.id}`}
        >
          <Trash2 size={13} />
        </button>
      </div>
    );
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:w-[400px] p-0 flex flex-col" data-testid="task-panel">
        <SheetHeader className="px-4 py-3 border-b flex-shrink-0">
          <div className="flex items-center justify-between">
            <SheetTitle className="text-base font-semibold">Taches</SheetTitle>
            {isAdmin && (
              <Button
                variant={showAll ? "default" : "outline"}
                size="sm"
                className="h-7 text-[11px]"
                onClick={() => setShowAll(!showAll)}
                data-testid="task-show-all-toggle"
              >
                {showAll ? "Toutes" : "Les miennes"}
              </Button>
            )}
          </div>
        </SheetHeader>

        {/* Quick add */}
        <div className="px-4 py-3 border-b flex-shrink-0 bg-slate-50/50">
          <div className="flex gap-2">
            <Input
              placeholder="Nouvelle tache..."
              value={newTitle}
              onChange={e => setNewTitle(e.target.value)}
              onKeyDown={e => e.key === "Enter" && !expandedNew && handleAdd()}
              className="h-9 text-sm"
              data-testid="task-new-title"
            />
            <Button size="sm" className="h-9 px-3" onClick={() => expandedNew ? handleAdd() : (newTitle.trim() ? handleAdd() : null)} disabled={loading || !newTitle.trim()} data-testid="task-add-btn">
              <Plus size={14} />
            </Button>
          </div>
          <button
            onClick={() => setExpandedNew(!expandedNew)}
            className="flex items-center gap-1 mt-1.5 text-[11px] text-slate-400 hover:text-slate-600"
          >
            {expandedNew ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
            {expandedNew ? "Moins d'options" : "Plus d'options"}
          </button>
          {expandedNew && (
            <div className="mt-2 space-y-2">
              <Input
                placeholder="Description (optionnel)"
                value={newForm.description}
                onChange={e => setNewForm(p => ({ ...p, description: e.target.value }))}
                className="h-8 text-xs"
                data-testid="task-new-description"
              />
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] text-slate-400 uppercase">Échéance</label>
                  <DateField value={newForm.due_date} onChange={e => setNewForm(p => ({ ...p, due_date: e.target.value }))} className="h-8 text-xs" data-testid="task-new-date" />
                </div>
                <div>
                  <label className="text-[10px] text-slate-400 uppercase">Priorite</label>
                  <Select value={newForm.priority} onValueChange={v => setNewForm(p => ({ ...p, priority: v }))}>
                    <SelectTrigger className="h-8 text-xs" data-testid="task-new-priority"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="haute"><span className="flex items-center gap-1.5"><Flag size={10} className="text-red-500" />Haute</span></SelectItem>
                      <SelectItem value="normale">Normale</SelectItem>
                      <SelectItem value="basse">Basse</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {users.length > 1 && (
                <div>
                  <label className="text-[10px] text-slate-400 uppercase">Assigner a</label>
                  <Select value={newForm.assigned_to} onValueChange={v => setNewForm(p => ({ ...p, assigned_to: v }))}>
                    <SelectTrigger className="h-8 text-xs" data-testid="task-new-assign"><SelectValue placeholder="Moi" /></SelectTrigger>
                    <SelectContent>
                      {users.map(u => <SelectItem key={u.user_id} value={u.user_id}>{u.name || u.email}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {/* Client link */}
              <div>
                <label className="text-[10px] text-slate-400 uppercase">Lier a un client</label>
                {newForm.client_id ? (
                  <div className="flex items-center gap-2 h-8 px-2 bg-indigo-50 rounded border border-indigo-200 text-xs text-indigo-700">
                    <Link2 size={11} />
                    <span className="flex-1 truncate">{newForm.client_name}</span>
                    <button onClick={clearClient} className="text-slate-400 hover:text-red-500"><X size={12} /></button>
                  </div>
                ) : (
                  <div className="relative">
                    <Input
                      placeholder="Rechercher un client..."
                      value={clientSearch}
                      onChange={e => setClientSearch(e.target.value)}
                      className="h-8 text-xs"
                      data-testid="task-new-client-search"
                    />
                    {clientResults.length > 0 && (
                      <div className="absolute top-full left-0 right-0 mt-0.5 bg-white border rounded-md shadow-lg z-10 max-h-32 overflow-y-auto">
                        {clientResults.map(c => (
                          <button
                            key={c.id}
                            onClick={() => selectClient(c)}
                            className="w-full text-left px-3 py-1.5 text-xs hover:bg-slate-50 border-b last:border-0"
                          >
                            {c.company || `${c.first_name || ""} ${c.last_name || ""}`.trim()}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
              {/* Pre-filled document (read-only, set from document row) */}
              {newForm.document_id && (
                <div>
                  <label className="text-[10px] text-slate-400 uppercase">Document lie</label>
                  <div className="flex items-center gap-2 h-8 px-2 bg-emerald-50 rounded border border-emerald-200 text-xs text-emerald-700">
                    <FileText size={11} />
                    <span className="flex-1 truncate">{newForm.document_number}</span>
                    <button onClick={() => setNewForm(p => ({ ...p, document_id: "", document_number: "" }))} className="text-slate-400 hover:text-red-500"><X size={12} /></button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Task list */}
        <div className="flex-1 overflow-y-auto px-2 py-2">
          {pendingTasks.length === 0 && !showDone && (
            <div className="text-center py-10 text-slate-400">
              <CheckSquare size={32} className="mx-auto mb-2 text-slate-300" />
              <p className="text-sm">Aucune tache en cours</p>
            </div>
          )}
          {pendingTasks.map(t => <TaskItem key={t.id} task={t} />)}

          {doneTasks.length > 0 && (
            <div className="mt-3">
              <button
                onClick={() => setShowDone(!showDone)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-slate-400 hover:text-slate-600 w-full"
                data-testid="task-show-done-toggle"
              >
                {showDone ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                {doneTasks.length} terminee{doneTasks.length > 1 ? "s" : ""}
              </button>
              {showDone && doneTasks.map(t => <TaskItem key={t.id} task={t} />)}
            </div>
          )}
        </div>

        {/* Footer stats */}
        <div className="px-4 py-2 border-t text-[11px] text-slate-400 flex-shrink-0">
          {pendingTasks.length} en cours · {doneTasks.length} terminee{doneTasks.length > 1 ? "s" : ""}
        </div>
      </SheetContent>
    </Sheet>
  );
}
